// src/pages/WhitepaperPage.js
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  FaAndroid, FaShieldAlt, FaUsers, FaBolt,
  FaDownload, FaExternalLinkAlt,
} from "react-icons/fa";

/* ── Animations ─────────────────────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

/* ── Page ───────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: linear-gradient(160deg, #f0f9ff 0%, #ffffff 55%, #fef6e4 100%);
  color: var(--text-color);
  min-height: 100vh;
`;

/* ── Hero ───────────────────────────────────────────────────────────────────── */
const Hero = styled.section`
  padding: 4rem 8% 3.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
`;

const HeroInner = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.6fr;
  gap: 3rem;
  align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(41,171,226,0.1);
  border: 1px solid rgba(41,171,226,0.25);
  color: var(--primary-dark);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.3rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.1rem;
`;

const HeroH1 = styled.h1`
  font-size: clamp(2.2rem, 4.5vw, 3.4rem);
  line-height: 1.1;
  margin: 0 0 1rem;
  background: linear-gradient(135deg, var(--navy-color) 0%, var(--primary-dark) 55%, var(--accent-color) 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 5s linear infinite;
`;

const HeroSub = styled.p`
  font-size: 1.05rem;
  color: var(--text-secondary-color);
  line-height: 1.75;
  max-width: 600px;
  margin: 0 0 1.8rem;
`;

const HeroBtnRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: #ffffff;
  font-weight: 700;
  font-size: 0.88rem;
  padding: 0.72rem 1.4rem;
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(41,171,226,0.35); }
`;

const BtnGhost = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #ffffff;
  border: 1px solid var(--border-color);
  color: var(--text-secondary-color);
  font-weight: 700;
  font-size: 0.88rem;
  padding: 0.72rem 1.4rem;
  border-radius: 12px;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover { border-color: var(--primary-color); box-shadow: 0 4px 16px rgba(41,171,226,0.15); }
`;

const Disclaimer = styled.div`
  margin-top: 1.6rem;
  padding: 1rem 1.2rem;
  background: rgba(247,148,29,0.06);
  border: 1px solid rgba(247,148,29,0.22);
  border-left: 3px solid var(--accent-color);
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--text-secondary-color);
  line-height: 1.65;
  strong { color: var(--accent-color); }
`;

/* Token snapshot card */
const TokenCard = styled.div`
  background: linear-gradient(145deg, var(--navy-color) 0%, var(--primary-dark) 100%);
  border-radius: 18px;
  padding: 1.6rem;
  color: #ffffff;
  position: sticky;
  top: 90px;
`;

const TokenCardTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  margin: 0 0 1.1rem;
`;

const TokenList = styled.ul`
  list-style: none;
  margin: 0 0 1.1rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const TokenListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  &:last-child { border-bottom: none; padding-bottom: 0; }
  span { color: rgba(255,255,255,0.55); }
  strong { color: #ffffff; font-weight: 700; text-align: right; max-width: 55%; }
`;

const TokenNote = styled.p`
  font-size: 0.74rem;
  color: rgba(255,255,255,0.5);
  line-height: 1.6;
  margin: 0;
`;

/* ── Main layout ─────────────────────────────────────────────────────────────── */
const Main = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 0;
  align-items: start;
  padding: 0 8%;
  max-width: 1400px;
  margin: 0 auto;
  @media (max-width: 900px) { grid-template-columns: 1fr; padding: 0 5%; }
`;

/* ── TOC ─────────────────────────────────────────────────────────────────────── */
const TOC = styled.nav`
  position: sticky;
  top: 80px;
  padding: 2.5rem 1.5rem 2.5rem 0;
  @media (max-width: 900px) { display: none; }
`;

const TOCTitle = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-body-soft);
  margin: 0 0 0.9rem;
`;

const TOCList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const TOCItem = styled.li`
  a {
    display: block;
    font-size: 0.78rem;
    color: var(--text-secondary-color);
    text-decoration: none;
    padding: 0.28rem 0.5rem;
    border-radius: 5px;
    border-left: 2px solid transparent;
    line-height: 1.4;
    transition: color 0.15s, border-color 0.15s, background 0.15s;
    &:hover {
      color: var(--primary-dark);
      border-left-color: var(--primary-color);
      background: rgba(41,171,226,0.06);
    }
  }
  &.sub a { padding-left: 1.2rem; font-size: 0.73rem; }
`;

/* ── Article body ────────────────────────────────────────────────────────────── */
const Article = styled.article`
  padding: 3rem 0 5rem 3rem;
  border-left: 1px solid var(--border-color);
  @media (max-width: 900px) { padding: 2rem 0 4rem; border-left: none; }
`;

const Sec = styled.section`
  margin-bottom: 3.5rem;
  scroll-margin-top: 90px;
`;

const H2 = styled.h2`
  font-size: 1.7rem;
  margin: 0 0 1rem;
  color: var(--navy-color);
  padding-bottom: 0.6rem;
  border-bottom: 2px solid var(--border-color);
`;

const H3 = styled.h3`
  font-size: 1.15rem;
  margin: 1.6rem 0 0.6rem;
  color: var(--text-color);
`;

const P = styled.p`
  color: var(--text-secondary-color);
  line-height: 1.8;
  margin: 0 0 0.9rem;
  font-size: 0.97rem;
`;

const UL = styled.ul`
  color: var(--text-secondary-color);
  line-height: 1.8;
  margin: 0 0 0.9rem;
  padding-left: 1.4rem;
  font-size: 0.97rem;
  li { margin-bottom: 0.35rem; }
`;

const OL = styled.ol`
  color: var(--text-secondary-color);
  line-height: 1.8;
  margin: 0 0 0.9rem;
  padding-left: 1.4rem;
  font-size: 0.97rem;
  li { margin-bottom: 0.35rem; }
`;

const InternalLink = styled(Link)`
  color: var(--primary-dark);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid rgba(41,171,226,0.35);
  transition: border-color 0.15s;
  &:hover { border-color: var(--primary-color); }
`;

const ExternalA = styled.a`
  color: var(--primary-dark);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid rgba(41,171,226,0.35);
  &:hover { border-color: var(--primary-color); }
`;

/* Product cards inside whitepaper */
const ProductBlock = styled.div`
  background: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.6rem;
  margin: 1.4rem 0;
  border-left: 4px solid ${({ $color }) => $color || 'var(--primary-color)'};
`;

const ProductBlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.9rem;
`;

const ProductIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${({ $color }) => `${$color}15`};
  border: 1px solid ${({ $color }) => `${$color}30`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ProductBlockTitle = styled.h4`
  font-size: 1rem;
  font-weight: 800;
  margin: 0;
  color: var(--text-color);
`;

const StatusBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  padding: 0.15rem 0.55rem;
  border-radius: 20px;
  background: ${({ $live }) => $live ? 'rgba(5,150,105,0.1)' : 'rgba(247,148,29,0.1)'};
  color: ${({ $live }) => $live ? '#059669' : '#d97706'};
  border: 1px solid ${({ $live }) => $live ? 'rgba(5,150,105,0.25)' : 'rgba(247,148,29,0.25)'};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* Tier grid */
const TierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const TierCard = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.1rem 1.2rem;
`;

const TierTitle = styled.h4`
  font-size: 0.88rem;
  font-weight: 800;
  margin: 0 0 0.6rem;
  color: var(--navy-color);
`;

/* Roadmap grid */
const RoadmapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const RoadmapCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.2rem;
  border-top: 3px solid ${({ $done }) => $done ? '#059669' : 'var(--primary-color)'};
`;

const RoadmapPhase = styled.h4`
  font-size: 0.88rem;
  font-weight: 800;
  margin: 0 0 0.7rem;
  color: var(--text-color);
`;

/* Tokenomics table */
const AllocGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const AllocCard = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.1rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const AllocPct = styled.span`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--navy-color);
  line-height: 1;
`;

const AllocLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-color);
`;

const AllocDesc = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary-color);
  line-height: 1.5;
`;

/* ── Component ──────────────────────────────────────────────────────────────── */
const WhitepaperPage = () => {
  const whitepaperSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.trendgenie.io/whitepaper",
    },
    headline: "Trend Genie Whitepaper — AI Crypto Signals, RugShield, Social, Nova & TGEN Token",
    description:
      "Official Trend Genie whitepaper covering every product in the ecosystem: the AI Crypto Signals Android app, RugShield DeFi scanner, Trend Genie Social, Nova intelligence platform, and the TGEN utility token on BNB Smart Chain.",
    keywords:
      "Trend Genie whitepaper, AI crypto signals app, RugShield DeFi scanner, TGEN token, BEP-20 utility token, crypto trading signals Android",
    author: { "@type": "Organization", name: "Trend Genie", url: "https://www.trendgenie.io" },
    publisher: {
      "@type": "Organization",
      name: "Trend Genie",
      logo: { "@type": "ImageObject", url: "https://www.trendgenie.io/logo.png" },
    },
    datePublished: "2026-03-27T00:00:00+05:00",
    dateModified: "2026-03-27T00:00:00+05:00",
  };

  const sections = [
    { id: "intro",          label: "1. Introduction" },
    { id: "problem",        label: "2. The Problem" },
    { id: "approach",       label: "3. Our Approach" },
    { id: "products",       label: "4. Product Ecosystem" },
    { id: "android-app",    label: "4.1 AI Crypto Signals App", sub: true },
    { id: "rugshield",      label: "4.2 RugShield Scanner", sub: true },
    { id: "social",         label: "4.3 Trend Genie Social", sub: true },
    { id: "nova",           label: "4.4 Trend Genie Nova", sub: true },
    { id: "tgen-overview",  label: "5. TGEN Token Overview" },
    { id: "access-model",   label: "6. Access Model & Tiers" },
    { id: "token-utility",  label: "7. Token Utility" },
    { id: "tokenomics",     label: "8. Token Economics" },
    { id: "roadmap",        label: "9. Roadmap" },
    { id: "risks",          label: "10. Risks & Limitations" },
    { id: "summary",        label: "11. Summary" },
  ];

  return (
    <Page>
      <Helmet>
        <title>Trend Genie Whitepaper — AI Crypto Signals, RugShield, TGEN Token | Official Docs</title>
        <meta
          name="description"
          content="Read the official Trend Genie whitepaper. Full breakdown of the AI Crypto Signals Android app, RugShield DeFi scanner, Trend Genie Social, Nova platform, and the TGEN utility token on BNB Smart Chain."
        />
        <meta
          name="keywords"
          content="Trend Genie whitepaper, AI crypto signals app Android, RugShield DeFi scanner, TGEN token BEP-20, BNB Smart Chain utility token, crypto trading signals whitepaper"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.trendgenie.io/whitepaper" />
        <script type="application/ld+json">{JSON.stringify(whitepaperSchema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <Hero>
        <HeroInner>
          <div>
            <Eyebrow>Official Whitepaper · v2.0</Eyebrow>
            <HeroH1>Trend Genie Ecosystem — Full Product Overview &amp; TGEN Token</HeroH1>
            <HeroSub>
              The complete technical and product overview of the Trend Genie ecosystem, covering every live
              and upcoming product, the TGEN utility token, access tiers, tokenomics, and the long-term roadmap.
            </HeroSub>
            <HeroBtnRow>
              <BtnPrimary href="/trendgenie-whitepaper.pdf" target="_blank" rel="noreferrer">
                <FaDownload /> Download PDF
              </BtnPrimary>
              <BtnGhost
                href="https://bscscan.com/token/0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1"
                target="_blank"
                rel="noreferrer"
              >
                <FaExternalLinkAlt /> TGEN on BscScan
              </BtnGhost>
            </HeroBtnRow>
            <Disclaimer>
              <strong>Disclaimer:</strong> This document is for informational purposes only. Nothing in this
              whitepaper constitutes financial advice, investment advice, or a solicitation to buy any asset.
              Crypto and token investments carry significant risk. Always conduct your own research.
            </Disclaimer>
          </div>

          <TokenCard>
            <TokenCardTitle>TGEN Token Snapshot</TokenCardTitle>
            <TokenList>
              <TokenListItem><span>Token Name</span><strong>Trend Genie</strong></TokenListItem>
              <TokenListItem><span>Ticker</span><strong>$TGEN</strong></TokenListItem>
              <TokenListItem><span>Standard</span><strong>BEP-20</strong></TokenListItem>
              <TokenListItem><span>Network</span><strong>BNB Smart Chain</strong></TokenListItem>
              <TokenListItem><span>Total Supply</span><strong>500,000,000</strong></TokenListItem>
              <TokenListItem><span>Role</span><strong>Utility &amp; Access Token</strong></TokenListItem>
              <TokenListItem><span>Contract</span><strong>0xdD3f…30E1</strong></TokenListItem>
            </TokenList>
            <TokenNote>
              $TGEN is a utility token used for access tiers and future governance across Trend Genie tools.
              It does not represent equity or a right to revenue or profits.
            </TokenNote>
          </TokenCard>
        </HeroInner>
      </Hero>

      {/* ── MAIN: TOC + BODY ── */}
      <Main>
        <TOC>
          <TOCTitle>Contents</TOCTitle>
          <TOCList>
            {sections.map((s) => (
              <TOCItem key={s.id} className={s.sub ? "sub" : ""}>
                <a href={`#${s.id}`}>{s.label}</a>
              </TOCItem>
            ))}
          </TOCList>
        </TOC>

        <Article>

          {/* 1. INTRODUCTION */}
          <Sec id="intro">
            <H2>1. Introduction</H2>
            <P>
              Trend Genie is a multi-product fintech ecosystem built to make crypto trading and DeFi exploration
              safer, smarter, and more accessible — without assuming you already know everything. Since launch,
              the ecosystem has grown from a single Android app into a suite of tools covering signal generation,
              token safety, community, and advanced market intelligence.
            </P>
            <P>
              The four products that make up the Trend Genie ecosystem today are:
            </P>
            <UL>
              <li>
                <strong>Trend Genie AI Crypto Signals App</strong> — a dedicated Android app delivering
                real-time AI-powered trade signals with entry, TP1, TP2, and stop-loss for Bitcoin, Ethereum,
                and a wide range of altcoins
              </li>
              <li>
                <strong>RugShield DeFi Scanner</strong> — a free web-based AI safety scanner that checks
                any ERC-20 or BEP-20 token contract for rug pull patterns, honeypots, and risky ownership
                structures in seconds
              </li>
              <li>
                <strong>Trend Genie Social</strong> — a crypto-focused community platform where traders
                share ideas, discuss coins, follow market narratives, and earn TGEN rewards through participation
              </li>
              <li>
                <strong>Trend Genie Nova</strong> — the upcoming advanced AI intelligence platform offering
                deeper market analytics and premium tooling for serious traders
              </li>
            </UL>
            <P>
              The <strong>$TGEN utility token</strong> on BNB Smart Chain (BEP-20) connects all of these
              products through a token-gated access tier system, rewarding active users and aligning long-term
              ecosystem participants with the growth of the platform.
            </P>
          </Sec>

          {/* 2. THE PROBLEM */}
          <Sec id="problem">
            <H2>2. The Problem We Solve</H2>
            <P>
              The typical retail crypto trader faces three compounding problems simultaneously:
            </P>
            <H3>Information Overload</H3>
            <P>
              Hundreds of charts, indicators, social media noise, and conflicting signals make it nearly
              impossible for a new or part-time trader to make structured decisions. Most tools surface data
              without context, leaving traders to interpret everything themselves.
            </P>
            <H3>Security Blind Spots</H3>
            <P>
              Rug pulls, honeypots, and malicious contract functions drain millions from retail traders every
              week. Most people can read a price chart but cannot manually audit a smart contract under the
              pressure of a fast-moving market. The result is that even experienced traders get caught by
              basic DeFi scams.
            </P>
            <H3>Isolated Trading Without Community</H3>
            <P>
              Trading alone breeds poor habits. Without a structured community tied to real tools,
              traders end up in low-quality Telegram groups or general social media feeds where most
              advice is noise or deliberate manipulation.
            </P>
            <H3>Paywall Fatigue</H3>
            <P>
              Most professional-grade crypto tools are locked behind expensive monthly subscriptions
              that are hard to justify for students, small accounts, or traders who are still learning.
              The result is that beginners either overpay for tools they do not fully use, or they go
              without and rely on guesswork.
            </P>
          </Sec>

          {/* 3. OUR APPROACH */}
          <Sec id="approach">
            <H2>3. Our Approach</H2>
            <P>Trend Genie is built on three core principles that run through every product in the ecosystem:</P>
            <H3>Education First</H3>
            <P>
              Every tool is designed to explain what it is showing you — not just show numbers. The AI Signals
              App provides context around entries and targets. RugShield explains each risk flag in plain English
              or technical detail depending on your preference. The goal is to make you a better trader, not
              just a faster one.
            </P>
            <H3>Security By Default</H3>
            <P>
              RugShield never asks for wallet connections, private keys, or transaction approvals. The signals
              app never asks for exchange API keys. Read-only tools that give you information without touching
              your funds are a foundational design principle across the ecosystem.
            </P>
            <H3>Token-Gated Power Features, Not Basic Access</H3>
            <P>
              Core tools and education are accessible to everyone. The $TGEN token unlocks depth, frequency,
              and advanced capabilities for power users — but it does not lock out people who are still
              learning. Anyone should be able to try the essential features before deciding to commit further.
            </P>
          </Sec>

          {/* 4. PRODUCTS */}
          <Sec id="products">
            <H2>4. The Trend Genie Product Ecosystem</H2>
            <P>
              The Trend Genie ecosystem consists of four distinct but interconnected products, each solving
              a specific part of the trader workflow. Together they form a complete loop from signal discovery
              to safety verification, community validation, and advanced analysis.
            </P>
          </Sec>

          {/* 4.1 ANDROID APP */}
          <Sec id="android-app">
            <H3>4.1 Trend Genie AI Crypto Signals App — Android</H3>
            <ProductBlock $color="#29ABE2">
              <ProductBlockHeader>
                <ProductIcon $color="#29ABE2"><FaAndroid /></ProductIcon>
                <div>
                  <ProductBlockTitle>Trend Genie AI Crypto Signals App</ProductBlockTitle>
                  <StatusBadge $live>Live on Google Play</StatusBadge>
                </div>
              </ProductBlockHeader>
              <P>
                The flagship product of the Trend Genie ecosystem. Available on Android through Google Play,
                this app delivers real-time AI-powered crypto trading signals with precise entry points,
                TP1 and TP2 take-profit targets, and stop-loss levels for Bitcoin, Ethereum, and a wide
                range of altcoins.
              </P>
            </ProductBlock>

            <H3>Overview</H3>
            <P>
              The <InternalLink to="/pricing">Trend Genie AI Crypto Signals App</InternalLink> is built for
              traders who want a mobile-first workflow that removes emotion and guesswork from decision-making.
              Instead of spending hours scanning charts, the AI engine monitors price action, market structure,
              and market sentiment continuously and delivers structured, actionable setups directly to your
              Android device through push notifications.
            </P>
            <P>
              The app reports an <strong>83%+ win rate</strong> on tracked signals. Unlike vague alerts that
              just tell you a coin is moving, every Trend Genie signal includes the complete trade setup so
              you know exactly what you are looking at before acting.
            </P>

            <H3>Core Features</H3>
            <UL>
              <li>Real-time AI signal engine with exact entry, TP1, TP2, and stop-loss levels</li>
              <li>Instant push notifications when high-probability setups appear</li>
              <li>Live charts, trend views, and crypto heatmap for fast market scanning</li>
              <li>Personalized watchlists and portfolio tracker</li>
              <li>Market scanner, signal feed, and active signal monitoring</li>
              <li>Live derivatives pulse — funding rates, long/short ratios, and open interest (30s refresh)</li>
              <li>Real-time Fibonacci retracement levels for BTC, ETH, SOL, and BNB (30-day swing)</li>
              <li>AI-curated crypto news feed with sentiment and impact scoring</li>
              <li>Beginner-friendly Android interface with fast loading and clean structure</li>
            </UL>

            <H3>Who It Is Built For</H3>
            <UL>
              <li>Beginner traders learning to spot trends without overwhelm</li>
              <li>Experienced traders who want faster, data-backed trade setups</li>
              <li>Busy professionals who need structured alerts on the go</li>
              <li>Android users who want a complete mobile-first trading workflow</li>
            </UL>

            <H3>Pricing &amp; Access</H3>
            <UL>
              <li>Available on Android — <ExternalA href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie" target="_blank" rel="noopener noreferrer">download on Google Play</ExternalA></li>
              <li>Free tier available with limited daily signals</li>
              <li>Premium plan: $9.99/month with full signal access, derivatives data, Fibonacci levels, unlimited alerts, and AI stock and forex signals</li>
              <li>7-day free trial on the Premium plan — no charge until the trial ends</li>
              <li>Cancel anytime through Google Play</li>
            </UL>
            <P>
              See the full plan comparison on the <InternalLink to="/pricing">Pricing page</InternalLink> or
              read the <InternalLink to="/insights/best-crypto-trading-app-android">guide to choosing the best crypto trading app for Android</InternalLink>.
            </P>
          </Sec>

          {/* 4.2 RUGSHIELD */}
          <Sec id="rugshield">
            <H3>4.2 RugShield — DeFi Safety Scanner</H3>
            <ProductBlock $color="#10b981">
              <ProductBlockHeader>
                <ProductIcon $color="#10b981"><FaShieldAlt /></ProductIcon>
                <div>
                  <ProductBlockTitle>RugShield DeFi Scanner</ProductBlockTitle>
                  <StatusBadge $live>Live at app.trendgenie.io</StatusBadge>
                </div>
              </ProductBlockHeader>
              <P>
                A free AI-assisted DeFi safety scanner that turns any ERC-20 or BEP-20 token contract into a
                clear risk assessment in seconds — without requiring any wallet connection.
              </P>
            </ProductBlock>

            <H3>Overview</H3>
            <P>
              RugShield is Trend Genie's free safety layer for DeFi traders. It supports Ethereum and BNB Chain,
              delivers results in 2–5 seconds, and presents findings through a traffic-light risk system
              (green / amber / red) that is immediately understandable regardless of technical background.
              Two explanation modes make it useful for both newcomers and experienced DeFi users:
            </P>
            <UL>
              <li><strong>Beginner Mode</strong> — translates each risk flag into plain English so new traders understand what they are looking at</li>
              <li><strong>Pro Mode</strong> — focuses on technical signal cards for expert review without simplified language</li>
            </UL>
            <P>
              RugShield uses only read-only on-chain data and external risk APIs. It never asks for a private
              key, wallet signature, or transaction approval. You can scan any contract address without putting
              any funds at risk.
            </P>

            <H3>What RugShield Checks</H3>
            <UL>
              <li>Honeypot detection — verifies that you can actually sell the token after buying</li>
              <li>Ownership analysis — flags contracts where ownership has not been renounced or where admin functions could drain liquidity</li>
              <li>Rug pull pattern detection — checks for known patterns used in fraudulent token launches</li>
              <li>Liquidity and contract risk flags across multiple risk categories</li>
            </UL>

            <H3>Roadmap Additions</H3>
            <P>
              Upcoming features include LP lock status and expiry, top-holder distribution and whale
              warnings, deployer history analysis, liquidity health scoring, and behavioral alerts for
              suspicious on-chain activity.
            </P>

            <H3>Access</H3>
            <UL>
              <li>Completely free — available at <ExternalA href="https://app.trendgenie.io" target="_blank" rel="noopener noreferrer">app.trendgenie.io</ExternalA></li>
              <li>No account or wallet connection required for basic scanning</li>
              <li>TGEN holders unlock higher daily scan limits and priority queue access</li>
            </UL>
            <P>
              Learn how to use RugShield alongside the signals app in the{" "}
              <InternalLink to="/insights/crypto-signals-for-beginners">crypto signals beginner guide</InternalLink>.
            </P>
          </Sec>

          {/* 4.3 SOCIAL */}
          <Sec id="social">
            <H3>4.3 Trend Genie Social — Community Platform</H3>
            <ProductBlock $color="#8B5CF6">
              <ProductBlockHeader>
                <ProductIcon $color="#8B5CF6"><FaUsers /></ProductIcon>
                <div>
                  <ProductBlockTitle>Trend Genie Social</ProductBlockTitle>
                  <StatusBadge $live>Live at social.trendgenie.io</StatusBadge>
                </div>
              </ProductBlockHeader>
              <P>
                A dedicated crypto community platform where traders share ideas, discuss coins, follow market
                narratives, and earn TGEN rewards through active participation — without the noise of general
                social media.
              </P>
            </ProductBlock>

            <H3>Overview</H3>
            <P>
              Trend Genie Social is the community and discussion layer of the ecosystem. Unlike Twitter or
              Reddit, where crypto discussions are mixed with unrelated content and low-quality speculation,
              Trend Genie Social is purpose-built for the same traders who use the signals app and RugShield.
              This means conversations are more focused, more relevant, and more actionable.
            </P>

            <H3>Core Features</H3>
            <UL>
              <li>Crypto-focused community feed built for active traders</li>
              <li>Post updates, share trade ideas, and react to community content</li>
              <li>Discussion threads around specific coins and market narratives</li>
              <li>Referral system and activity-driven TGEN token reward earning</li>
              <li>Integration with the wider Trend Genie product ecosystem</li>
            </UL>

            <H3>TGEN Rewards on Social</H3>
            <P>
              Active participation on Trend Genie Social connects directly with the TGEN token reward
              system. Referring new users and staying engaged in the community are key ways to accumulate
              TGEN tokens. Higher TGEN balances then unlock deeper capabilities across the entire ecosystem,
              creating a positive feedback loop between participation and utility.
            </P>

            <H3>Access</H3>
            <UL>
              <li>Free to join at <ExternalA href="https://social.trendgenie.io" target="_blank" rel="noopener noreferrer">social.trendgenie.io</ExternalA></li>
              <li>Connect a wallet for full ecosystem participation and rewards</li>
              <li>Integrated with the TGEN token reward and airdrop system</li>
            </UL>
          </Sec>

          {/* 4.4 NOVA */}
          <Sec id="nova">
            <H3>4.4 Trend Genie Nova — Advanced AI Intelligence Platform</H3>
            <ProductBlock $color="#F7941D">
              <ProductBlockHeader>
                <ProductIcon $color="#F7941D"><FaBolt /></ProductIcon>
                <div>
                  <ProductBlockTitle>Trend Genie Nova</ProductBlockTitle>
                  <StatusBadge>Coming Soon</StatusBadge>
                </div>
              </ProductBlockHeader>
              <P>
                The next evolution of the Trend Genie ecosystem. Nova is an advanced AI intelligence platform
                offering deeper market analytics, expanded trading workflows, and premium tooling for serious
                traders who need more than standard crypto signals.
              </P>
            </ProductBlock>

            <H3>Overview</H3>
            <P>
              While the Android app handles real-time signal generation and RugShield covers DeFi safety,
              Nova is designed to operate at a deeper analytical level — providing more powerful AI-driven
              market intelligence, expanded workflows, and advanced tooling for traders who want institutional-grade
              analysis without institutional-grade pricing.
            </P>
            <P>
              Nova represents the natural next step for Trend Genie users who have graduated beyond the
              core signals experience and want deeper, more comprehensive market intelligence in one platform.
              It will be available at <ExternalA href="https://nova.trendgenie.io" target="_blank" rel="noopener noreferrer">nova.trendgenie.io</ExternalA> at launch.
            </P>

            <H3>Planned Capabilities</H3>
            <UL>
              <li>Advanced AI market intelligence beyond standard signal generation</li>
              <li>Expanded analytical workflows for deeper crypto market research</li>
              <li>Premium tooling designed for experienced and serious traders</li>
              <li>Designed to connect with existing Android app and RugShield workflows</li>
              <li>Highest-tier product in the Trend Genie ecosystem</li>
            </UL>
            <P>
              Follow <InternalLink to="/insights">Genie Insights</InternalLink> for Nova launch announcements
              and early access information.
            </P>
          </Sec>

          {/* 5. TGEN TOKEN */}
          <Sec id="tgen-overview">
            <H2>5. TGEN Token Overview</H2>
            <P>
              The <strong>$TGEN utility token</strong> is the connective layer across the entire Trend Genie
              ecosystem. It is a BEP-20 token deployed on BNB Smart Chain, and its primary purpose is to
              unlock higher usage tiers, advanced features, and future governance rights across all Trend Genie products.
            </P>
            <P>
              TGEN is <strong>not</strong> a security token. It does not represent equity, ownership in the
              company, or any right to profit distributions or revenue sharing. It is a digital utility asset
              used exclusively within the Trend Genie ecosystem.
            </P>

            <H3>Token Details</H3>
            <UL>
              <li><strong>Name:</strong> Trend Genie</li>
              <li><strong>Ticker:</strong> $TGEN</li>
              <li><strong>Standard:</strong> BEP-20 (BNB Smart Chain)</li>
              <li><strong>Total Supply:</strong> 500,000,000 TGEN (fixed, no new minting after deployment)</li>
              <li><strong>Contract:</strong> <ExternalA href="https://bscscan.com/token/0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1" target="_blank" rel="noopener noreferrer">0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1</ExternalA></li>
              <li><strong>Role:</strong> Utility and access token for the Trend Genie ecosystem</li>
            </UL>
            <P>
              Learn more and see live token data on the <InternalLink to="/tgen-token">$TGEN Token page</InternalLink>.
            </P>
          </Sec>

          {/* 6. ACCESS MODEL */}
          <Sec id="access-model">
            <H2>6. Access Model &amp; TGEN Holder Tiers</H2>
            <P>
              Trend Genie uses a token-gated tier system that grants progressively deeper access across all
              products based on TGEN wallet balance. Core education and basic safety checks are always accessible.
              Holding TGEN unlocks more depth, frequency, and convenience without locking out users who are
              still learning.
            </P>

            <H3>6.1 Free Tier — Open Access</H3>
            <P>For wallets with 0 TGEN or no wallet connected:</P>
            <UL>
              <li>Limited AI signal access via the Android app</li>
              <li>Up to 3 RugShield scans per day</li>
              <li>Basic community access on Trend Genie Social</li>
              <li>Live crypto news feed and market overview</li>
            </UL>

            <H3>6.2 Token-Gated Tiers</H3>
            <TierGrid>
              <TierCard>
                <TierTitle>Tier 0 — Guest</TierTitle>
                <UL>
                  <li>0 TGEN</li>
                  <li>3 free RugShield scans/day</li>
                  <li>Limited AI signals</li>
                  <li>Basic news &amp; market data</li>
                </UL>
              </TierCard>
              <TierCard>
                <TierTitle>Tier 1 — Explorer</TierTitle>
                <UL>
                  <li>≥ 1,000 TGEN</li>
                  <li>30 RugShield scans/day</li>
                  <li>Extended signal explanations</li>
                  <li>Higher daily app usage limits</li>
                </UL>
              </TierCard>
              <TierCard>
                <TierTitle>Tier 2 — Pro</TierTitle>
                <UL>
                  <li>≥ 10,000 TGEN</li>
                  <li>Unlimited RugShield scans (fair use)</li>
                  <li>Priority scanner queue</li>
                  <li>Early access to new features</li>
                  <li>Deeper analytics in the mobile app</li>
                </UL>
              </TierCard>
              <TierCard>
                <TierTitle>Tier 3 — Partner</TierTitle>
                <UL>
                  <li>≥ 50,000 TGEN</li>
                  <li>All Pro tier benefits</li>
                  <li>Private beta feature access</li>
                  <li>Eligibility for future governance &amp; advisory roles</li>
                </UL>
              </TierCard>
            </TierGrid>

            <H3>6.3 How The Tier Check Works</H3>
            <OL>
              <li>User connects or signs in with a wallet on app.trendgenie.io</li>
              <li>The backend reads the wallet's public address and checks the TGEN balance on-chain</li>
              <li>A tier is assigned based on the balance at the time of the request</li>
              <li>Each API call checks today's usage count for that wallet against the tier's limits</li>
              <li>Within limits — request proceeds. Limit exceeded — user sees an upgrade prompt</li>
            </OL>
            <P>
              No private keys are ever stored. Only the public address and usage counters are tracked
              server-side.
            </P>
          </Sec>

          {/* 7. TOKEN UTILITY */}
          <Sec id="token-utility">
            <H2>7. Token Utility Summary</H2>
            <P>$TGEN is designed to deliver utility across five dimensions:</P>
            <UL>
              <li>
                <strong>Usage &amp; Rate Limits:</strong> Higher TGEN tiers unlock more RugShield scans,
                more AI signals, more price alerts, and higher API call frequency across all tools.
              </li>
              <li>
                <strong>Advanced Features:</strong> Access to additional metrics, new chain support,
                premium risk checks, deeper AI explanations, and derivatives intelligence.
              </li>
              <li>
                <strong>Community Rewards:</strong> TGEN is earned through active participation on
                Trend Genie Social, successful referrals, community campaigns, and bug bounties.
              </li>
              <li>
                <strong>Early Access &amp; Beta Testing:</strong> Tier 2 and Tier 3 holders test new
                modules before public release and provide direct product feedback to the team.
              </li>
              <li>
                <strong>Future Governance (Planned):</strong> Non-binding community votes on supported
                chains, new features, tier threshold adjustments, and ecosystem direction — weighted by
                TGEN holdings.
              </li>
            </UL>
          </Sec>

          {/* 8. TOKENOMICS */}
          <Sec id="tokenomics">
            <H2>8. Token Economics</H2>
            <P>
              TGEN is a <strong>fixed-supply token</strong>. No new tokens will be minted after the
              initial deployment. Minting capability is permanently disabled or ownership renounced once
              initial distribution is complete, ensuring the supply cap of 500,000,000 TGEN is enforced on-chain.
            </P>
            <H3>Allocation Framework</H3>
            <AllocGrid>
              <AllocCard>
                <AllocPct>40%</AllocPct>
                <AllocLabel>Liquidity &amp; Market Making</AllocLabel>
                <AllocDesc>Seeding trading pools and ensuring reasonable liquidity depth on DEXes</AllocDesc>
              </AllocCard>
              <AllocCard>
                <AllocPct>25%</AllocPct>
                <AllocLabel>Community &amp; Rewards</AllocLabel>
                <AllocDesc>Airdrops, giveaways, learning quests, referral rewards, bug bounties, and educational campaigns</AllocDesc>
              </AllocCard>
              <AllocCard>
                <AllocPct>25%</AllocPct>
                <AllocLabel>Ecosystem &amp; Development Treasury</AllocLabel>
                <AllocDesc>Infrastructure costs (RPC, indexing, servers), R&amp;D, audits, and new feature development</AllocDesc>
              </AllocCard>
              <AllocCard>
                <AllocPct>10%</AllocPct>
                <AllocLabel>Team &amp; Long-Term Contributors</AllocLabel>
                <AllocDesc>Vested over multiple years to align team incentives with long-term ecosystem growth</AllocDesc>
              </AllocCard>
            </AllocGrid>
            <P>
              Full allocation addresses and vesting schedules are published separately in the Tokenomics
              document and are verifiable on <ExternalA href="https://bscscan.com/token/0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1" target="_blank" rel="noopener noreferrer">BscScan</ExternalA>.
            </P>
          </Sec>

          {/* 9. ROADMAP */}
          <Sec id="roadmap">
            <H2>9. Roadmap</H2>
            <RoadmapGrid>
              <RoadmapCard $done>
                <RoadmapPhase>Phase 1 — Foundations ✓ Complete</RoadmapPhase>
                <UL>
                  <li>Launch Trend Genie Android app on Google Play</li>
                  <li>Launch RugShield web scanner at app.trendgenie.io</li>
                  <li>Deploy $TGEN BEP-20 token on BNB Smart Chain</li>
                  <li>Launch Trend Genie Social at social.trendgenie.io</li>
                  <li>Publish whitepaper and core documentation</li>
                </UL>
              </RoadmapCard>
              <RoadmapCard>
                <RoadmapPhase>Phase 2 — Token Integration</RoadmapPhase>
                <UL>
                  <li>Add wallet-based login to app.trendgenie.io</li>
                  <li>Implement TGEN-based usage tiers across all products</li>
                  <li>Launch TGEN/BNB liquidity pool</li>
                  <li>Expand RugShield with LP lock status and deployer history</li>
                  <li>Social reward system fully live with referral tracking</li>
                </UL>
              </RoadmapCard>
              <RoadmapCard>
                <RoadmapPhase>Phase 3 — Nova &amp; Pro Features</RoadmapPhase>
                <UL>
                  <li>Launch Trend Genie Nova at nova.trendgenie.io</li>
                  <li>Expand chain support (additional EVM networks)</li>
                  <li>Advanced AI modules: portfolio stress testing, scenario analysis</li>
                  <li>Stock and forex AI signals fully live in the Android app</li>
                  <li>Community feature voting and proposal system launched</li>
                </UL>
              </RoadmapCard>
              <RoadmapCard>
                <RoadmapPhase>Phase 4 — Governance &amp; Ecosystem</RoadmapPhase>
                <UL>
                  <li>Formalize governance mechanisms for TGEN holders</li>
                  <li>Partner program for educators, research groups, and security communities</li>
                  <li>Explore integration with DeFi protocols and multi-chain wallets</li>
                  <li>iOS app launch for the Trend Genie signals product</li>
                </UL>
              </RoadmapCard>
            </RoadmapGrid>
          </Sec>

          {/* 10. RISKS */}
          <Sec id="risks">
            <H2>10. Risks &amp; Limitations</H2>
            <P>It is important to be transparent about what Trend Genie and TGEN cannot guarantee:</P>
            <UL>
              <li>
                <strong>No guaranteed profit.</strong> AI signals and risk checks are decision-support tools,
                not financial advice. Past signal performance does not guarantee future results.
              </li>
              <li>
                <strong>No perfect security detection.</strong> RugShield catches many common red flags but
                cannot detect every form of smart contract manipulation. It should be treated as one layer in
                a broader due-diligence process.
              </li>
              <li>
                <strong>Smart contract and infrastructure risk.</strong> Bugs, RPC outages, or third-party API
                issues may impact uptime or data accuracy. The $TGEN contract itself carries standard smart
                contract risks.
              </li>
              <li>
                <strong>Regulatory uncertainty.</strong> Crypto regulations continue to evolve across
                jurisdictions. Trend Genie will adapt features and product availability as needed to comply
                with applicable laws.
              </li>
              <li>
                <strong>Market volatility.</strong> The price of TGEN and all crypto assets can be extremely
                volatile. Access tiers should never be the primary reason to over-expose yourself financially
                to any single asset.
              </li>
              <li>
                <strong>TGEN is not equity.</strong> Holding TGEN does not grant ownership in Trend Genie,
                a right to company revenues, or a legal claim against the founding team.
              </li>
            </UL>
          </Sec>

          {/* 11. SUMMARY */}
          <Sec id="summary">
            <H2>11. Summary</H2>
            <P>
              Trend Genie exists to make crypto trading safer, clearer, and more educational for normal
              people — not just full-time quants and institutional players. The ecosystem addresses every
              critical stage of the retail trader workflow:
            </P>
            <UL>
              <li>
                <strong>Signal Discovery:</strong> The <InternalLink to="/pricing">AI Crypto Signals App</InternalLink> delivers
                real-time, structured trade setups with entry, TP1, TP2, and stop-loss directly to Android
              </li>
              <li>
                <strong>Safety Verification:</strong> <ExternalA href="https://app.trendgenie.io" target="_blank" rel="noopener noreferrer">RugShield</ExternalA> checks any
                ERC-20 or BEP-20 contract for rug pulls, honeypots, and risky ownership — free, in seconds, without a wallet connection
              </li>
              <li>
                <strong>Community &amp; Validation:</strong> <ExternalA href="https://social.trendgenie.io" target="_blank" rel="noopener noreferrer">Trend Genie Social</ExternalA> gives
                traders a focused community tied to real tools, where active participation earns TGEN rewards
              </li>
              <li>
                <strong>Advanced Intelligence:</strong> <ExternalA href="https://nova.trendgenie.io" target="_blank" rel="noopener noreferrer">Trend Genie Nova</ExternalA> (coming soon) will bring
                the next level of AI-powered market analysis for serious traders
              </li>
              <li>
                <strong>Token Layer:</strong> $TGEN connects the ecosystem through access tiers, community
                rewards, and future governance — creating alignment between engaged users and long-term platform growth
              </li>
            </UL>
            <P>
              Read the learning guides in <InternalLink to="/insights">Genie Insights</InternalLink>,
              explore live <InternalLink to="/news">market intelligence</InternalLink>, or
              download the app to get started.
            </P>
            <P>
              For collaboration enquiries — builders, educators, traders, or security researchers — contact
              the team at{" "}
              <ExternalA href="mailto:support@trendgenie.io">support@trendgenie.io</ExternalA>.
            </P>
          </Sec>

        </Article>
      </Main>
    </Page>
  );
};

export default WhitepaperPage;
